import { useState, useEffect } from 'react'
import { MdBusiness, MdAdd, MdFlight, MdSchedule, MdRefresh, MdTrendingUp } from 'react-icons/md'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import AirlineList from '../../components/airlines/AirlineList'
import {
  Button,
  LoadingSpinner,
  SearchBar,
  FilterBar,
  StatCard,
  EmptyState,
} from '../../components/ui'
import { useAirline } from '../../hooks/useAirline'
import { useWebsocket } from '../../hooks/useWebsocket'
import { addSubscription, removeSubscription } from '../../state/websocket.slice'
import {
  getAirlineStats,
  filterAirlinesBySearch,
  filterAirlinesByStatus,
  sortAirlines,
} from '../../utils/airline'
import { getWebsocketChannel } from '../../utils/websocket'

function AirlinesPage() {
  const dispatch = useDispatch()
  const [airlines, setAirlines] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortBy, setSortBy] = useState('name')
  const [sortOrder, setSortOrder] = useState('asc')
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    suspended: 0,
    totalFleetSize: 0,
    totalDestinations: 0,
    totalRevenue: 0,
    averageFleetSize: 0,
    averageRevenue: 0,
  })

  const navigate = useNavigate()
  const { getAirlinesAsync, isGetAirlinesLoading } = useAirline()
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()

  useEffect(() => {
    const channelName = getWebsocketChannel().airlineList

    if (channelName) {
      subscribeToChannel(channelName)
      dispatch(addSubscription(channelName))
    }

    loadAirlines()

    return () => {
      if (channelName) {
        unsubscribeFromChannel(channelName)
        dispatch(removeSubscription(channelName))
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const loadAirlines = async () => {
    try {
      setIsLoading(true)
      const airlinesData = await getAirlinesAsync()
      setAirlines(airlinesData || [])

      // Calculate stats
      const statsData = getAirlineStats(airlinesData || [])
      setStats(statsData)
    } catch (error) {
      console.error('Error loading airlines:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getFilteredAndSortedAirlines = () => {
    let filteredAirlines = airlines

    // Apply search filter
    if (searchTerm) {
      filteredAirlines = filterAirlinesBySearch(filteredAirlines, searchTerm)
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      filteredAirlines = filterAirlinesByStatus(filteredAirlines, statusFilter)
    }

    // Apply sorting
    filteredAirlines = sortAirlines(filteredAirlines, sortBy, sortOrder)

    return filteredAirlines
  }

  const filteredAirlines = getFilteredAndSortedAirlines()

  const handleAirlineClick = (airline) => {
    navigate(`/airlines/${airline._id || airline.id}`)
  }

  const handleAddAirline = () => {
    navigate('/airlines/new')
  }

  const handleRefresh = () => {
    loadAirlines()
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="text-left">
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
              Airline Management
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              Monitor and manage airline partnerships and operations
            </p>
          </div>

          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={handleRefresh}
              disabled={isGetAirlinesLoading}
              className="flex items-center space-x-2"
            >
              <MdRefresh className={isGetAirlinesLoading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </Button>

            <Button
              variant="primary"
              onClick={handleAddAirline}
              className="flex items-center space-x-2"
            >
              <MdAdd className="text-lg" />
              <span>Add Airline</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Airlines"
          value={stats.total}
          subtitle="Registered airlines"
          icon={MdBusiness}
          iconBgColor="bg-primary-100 dark:bg-primary-900"
          iconColor="text-primary-600 dark:text-primary-400"
        />

        <StatCard
          title="Active Airlines"
          value={stats.active}
          subtitle="Currently operating"
          icon={MdFlight}
          iconBgColor="bg-success-100 dark:bg-success-900"
          iconColor="text-success-600 dark:text-success-400"
        />

        <StatCard
          title="Total Fleet"
          value={stats.totalFleetSize}
          subtitle="Aircraft in service"
          icon={MdSchedule}
          iconBgColor="bg-info-100 dark:bg-info-900"
          iconColor="text-info-600 dark:text-info-400"
        />

        <StatCard
          title="Total Revenue"
          value={`$${(stats.totalRevenue / 1000000000).toFixed(1)}B`}
          subtitle="Combined annual revenue"
          icon={MdTrendingUp}
          iconBgColor="bg-warning-100 dark:bg-warning-900"
          iconColor="text-warning-600 dark:text-warning-400"
        />
      </div>

      {/* Filters and Search */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Airlines ({filteredAirlines.length})
          </h3>

          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search airlines..."
            />

            <div className="flex items-center space-x-3">
              <FilterBar
                filterValue={statusFilter}
                onFilterChange={(e) => setStatusFilter(e.target.value)}
                filterOptions={[
                  { value: 'all', label: 'All Airlines' },
                  { value: 'active', label: `Active (${stats.active})` },
                  { value: 'inactive', label: `Inactive (${stats.inactive})` },
                  { value: 'suspended', label: `Suspended (${stats.suspended})` },
                ]}
              />

              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split('-')
                  setSortBy(field)
                  setSortOrder(order)
                }}
                className="px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="name-asc">Name (A-Z)</option>
                <option value="name-desc">Name (Z-A)</option>
                <option value="fleetSize-desc">Fleet Size (High-Low)</option>
                <option value="fleetSize-asc">Fleet Size (Low-High)</option>
                <option value="revenue-desc">Revenue (High-Low)</option>
                <option value="revenue-asc">Revenue (Low-High)</option>
                <option value="destinations-desc">Destinations (High-Low)</option>
                <option value="destinations-asc">Destinations (Low-High)</option>
                <option value="founded-asc">Founded (Oldest)</option>
                <option value="founded-desc">Founded (Newest)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Airlines List */}
      {filteredAirlines.length === 0 && !isLoading ? (
        <EmptyState
          icon={MdBusiness}
          title="No airlines found"
          description={
            searchTerm || statusFilter !== 'all'
              ? 'No airlines match your current filters. Try adjusting your search criteria.'
              : 'Get started by adding your first airline to the system.'
          }
          action={
            searchTerm || statusFilter !== 'all' ? (
              <Button
                variant="primary"
                onClick={() => {
                  setSearchTerm('')
                  setStatusFilter('all')
                }}
              >
                Clear Filters
              </Button>
            ) : (
              <Button variant="primary" onClick={handleAddAirline}>
                Add Airline
              </Button>
            )
          }
        />
      ) : (
        <AirlineList airlines={filteredAirlines} onAirlineClick={handleAirlineClick} />
      )}
    </div>
  )
}

export default AirlinesPage
